import React, { useEffect, useState } from "react";
import PageHeader from "../../../components/page-layout/PageHeader";
import {
    Box,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Menu,
    MenuItem,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    Typography,
    TablePagination,
} from "@mui/material";
import { MoreHoriz } from "@mui/icons-material";
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import businessEnquiriesService from "../../../services/businessEnquiriesService";
import { toast } from "react-hot-toast";
import LoadingUI from "../../../components/LoadingUI"; // Adjust the import path as needed

const STATUS_OPTIONS = ["Resolved"];

const BusinessEnquiries = () => {
    const [rows, setRows] = useState([]);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const [anchorEl, setAnchorEl] = useState(null);
    const [menuIndex, setMenuIndex] = useState(null);
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const loadPage = async (pageNum = 0, limit = 10) => {
        setLoading(true);
        try {
            const res = await businessEnquiriesService.getBusinessEnquiries(
                pageNum + 1, 
                limit,
                'status',
                'asc'
            );
            
            if (res && res.data) {
                const sortedData = [...res.data].sort((a, b) => {
                    const aResolved = a.status === 'Resolved';
                    const bResolved = b.status === 'Resolved';
                    if (aResolved === bResolved) return 0;
                    return aResolved ? 1 : -1;
                });
                
                setRows(sortedData);
                setTotalCount(res.total || 0);
            }
        } catch (err) {
            console.error('Error loading enquiries:', err);
            toast.error("Failed to load business enquiries");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadPage(page, rowsPerPage);
    }, [page, rowsPerPage]);

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleMenuClick = (event, idx) => {
        setAnchorEl(event.currentTarget);
        setMenuIndex(idx);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setMenuIndex(null);
    };

    const handleStatusChange = async (idx, status) => {
        handleMenuClose();
        const row = rows[idx];
        if (!row) return;
        const old = row.status;
        const updated = [...rows];
        updated[idx] = { ...updated[idx], status };
        setRows(updated);
        try {
            await businessEnquiriesService.patchBusinessEnquiry(row.id, status);
            toast.success("Status updated successfully");
            loadPage(page, rowsPerPage);
        } catch (err) {
            updated[idx] = { ...updated[idx], status: old };
            setRows(updated);
            toast.error("Failed to update status");
        }
    };

    return (
        <Stack spacing={2}>
            <PageHeader title="Business Enquiries" lead="List of received business enquiries" />

            <Box>
                <Paper sx={{  minHeight: 700 }}>
                    {loading ? (
                        <Box 
                            sx={{ 
                                display: "flex", 
                                justifyContent: "center", 
                                alignItems: "center",
                                minHeight: 400
                            }}
                        >
                            <LoadingUI text="Loading enquiries..." />
                        </Box>
                    ) : (
                        <>
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Full Name</TableCell>
                                            <TableCell>Email</TableCell>
                                            <TableCell>Contact</TableCell>
                                            <TableCell>Message</TableCell>
                                            <TableCell>Status</TableCell>
                                            <TableCell align="center">Actions</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {rows.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} align="center">
                                                    <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                                                        No enquiries found
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            rows.map((r, idx) => (
                                                <TableRow 
                                                    key={r.id} 
                                                    hover
                                                    sx={{
                                                        backgroundColor: r.status === 'Unresolved' 
                                                            ? 'rgba(234, 179, 8, 0.08)' 
                                                            : 'transparent'
                                                    }}
                                                >
                                                    <TableCell>{r.fullName}</TableCell>
                                                    <TableCell>{r.email}</TableCell>
                                                    <TableCell>{r.contactNumber}</TableCell>
                                                    <TableCell 
                                                        sx={{ 
                                                            maxWidth: 300, 
                                                            overflow: 'hidden', 
                                                            textOverflow: 'ellipsis', 
                                                            whiteSpace: 'nowrap' 
                                                        }} 
                                                        title={r.message}
                                                    >
                                                        {r.message}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Chip 
                                                            label={r.status} 
                                                            color={r.status === 'Resolved' ? 'default' : 'primary'} 
                                                            size="small"
                                                            sx={{
                                                                ...(r.status === 'Resolved' && {
                                                                    backgroundColor: '#9e9e9e',
                                                                    color: '#fff'
                                                                })
                                                            }}
                                                        />
                                                    </TableCell>
                                                    <TableCell align="right">
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={() => { 
                                                                setSelectedRow(r); 
                                                                setOpenDialog(true); 
                                                            }}
                                                        >
                                                            <RemoveRedEyeIcon />
                                                        </IconButton>
                                                        <IconButton 
                                                            size="small" 
                                                            onClick={(e) => handleMenuClick(e, idx)}
                                                            disabled={r.status === 'Resolved'}
                                                        >
                                                            <MoreHoriz />
                                                        </IconButton>
                                                    </TableCell>
                                                </TableRow>
                                            ))
                                        )}
                                    </TableBody>
                                </Table>
                            </TableContainer>

                            <TablePagination
                                component="div"
                                count={totalCount}
                                page={page}
                                onPageChange={handleChangePage}
                                rowsPerPage={rowsPerPage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                rowsPerPageOptions={[5, 10, 25, 50]}
                                sx={{ borderTop: 1, borderColor: 'divider' }}
                            />
                        </>
                    )}
                </Paper>

                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                    {STATUS_OPTIONS.map((s) => (
                        <MenuItem 
                            key={s} 
                            onClick={() => handleStatusChange(menuIndex, s)}
                        >
                            {s}
                        </MenuItem>
                    ))}
                </Menu>

                <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
                    <DialogTitle>Enquiry Details</DialogTitle>
                    <DialogContent>
                        {selectedRow && (
                            <Box sx={{ pt: 1 }}>
                                <Typography variant="subtitle1" fontWeight={600}>
                                    {selectedRow.fullName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    {selectedRow.email} • {selectedRow.contactNumber}
                                </Typography>
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="body2" color="text.secondary" fontWeight={600}>
                                        Message:
                                    </Typography>
                                    <Typography sx={{ whiteSpace: 'pre-line', mt: 0.5 }}>
                                        {selectedRow.message}
                                    </Typography>
                                </Box>
                                <Box sx={{ mt: 2 }}>
                                    <Chip 
                                        label={selectedRow.status} 
                                        color={selectedRow.status === 'Resolved' ? 'default' : 'primary'} 
                                        size="small"
                                        sx={{
                                            ...(selectedRow.status === 'Resolved' && {
                                                backgroundColor: '#9e9e9e',
                                                color: '#fff'
                                            })
                                        }}
                                    />
                                </Box>
                            </Box>
                        )}
                    </DialogContent>
                </Dialog>
            </Box>
        </Stack>
    );
};

export default BusinessEnquiries;
